import { useState, FormEvent } from 'react';

export default function QuoteForm() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    serviceType: [] as string[],
    squareFootage: '',
    frequency: '',
    startDate: '',
    additionalRequirements: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const serviceTypes = [
    'Office Cleaning',
    'Retail Cleaning',
    'Medical Facility Cleaning',
    'Industrial Cleaning',
    'Window Cleaning',
    'Carpet Cleaning',
    'Floor Waxing',
    'Deep Cleaning'
  ];

  const handleServiceTypeChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      serviceType: prev.serviceType.includes(service)
        ? prev.serviceType.filter(s => s !== service)
        : [...prev.serviceType, service]
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Validate required fields
      if (!formData.companyName || !formData.contactPerson || !formData.email || !formData.phone) {
        alert('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }

      // Validate textarea length
      if (formData.additionalRequirements.length > 500) {
        alert('Additional requirements must be 500 characters or less');
        setIsSubmitting(false);
        return;
      }

      // Prepare form data
      const submitData = new URLSearchParams();
      submitData.append('companyName', formData.companyName);
      submitData.append('contactPerson', formData.contactPerson);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('serviceType', formData.serviceType.join(', '));
      submitData.append('squareFootage', formData.squareFootage);
      submitData.append('frequency', formData.frequency);
      submitData.append('startDate', formData.startDate);
      submitData.append('additionalRequirements', formData.additionalRequirements);

      const response = await fetch('https://readdy.ai/api/form/d63osiqbdoed7v0v8rj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          serviceType: [],
          squareFootage: '',
          frequency: '',
          startDate: '',
          additionalRequirements: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote-form" className="py-20 bg-slate-50">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-800 mb-4 font-['Poppins']">
            Get Your <strong className="text-teal-500">Free Quote</strong>
          </h2>
          <div className="w-16 h-1 bg-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-slate-600">
            Response within 2 hours during business hours
          </p>
        </div>

        {/* Form */}
        <form id="quote-request-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Company Name */}
            <div>
              <label htmlFor="companyName" className="block text-sm font-semibold text-slate-700 mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                required
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors text-sm"
                placeholder="Your company name"
              />
            </div>

            {/* Contact Person */}
            <div>
              <label htmlFor="contactPerson" className="block text-sm font-semibold text-slate-700 mb-2">
                Contact Person <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="contactPerson"
                name="contactPerson"
                required
                value={formData.contactPerson}
                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors text-sm"
                placeholder="Your full name"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors text-sm"
                placeholder="your@email.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors text-sm"
                placeholder="(123) 456-7890"
              />
            </div>

            {/* Square Footage */}
            <div>
              <label htmlFor="squareFootage" className="block text-sm font-semibold text-slate-700 mb-2">
                Square Footage
              </label>
              <input
                type="text"
                id="squareFootage"
                name="squareFootage"
                value={formData.squareFootage}
                onChange={(e) => setFormData({ ...formData, squareFootage: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors text-sm"
                placeholder="e.g., 5000 sq ft"
              />
            </div>

            {/* Frequency */}
            <div>
              <label htmlFor="frequency" className="block text-sm font-semibold text-slate-700 mb-2">
                Cleaning Frequency
              </label>
              <select
                id="frequency"
                name="frequency"
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors text-sm cursor-pointer"
              >
                <option value="">Select frequency</option>
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Bi-weekly">Bi-weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="One-time">One-time</option>
              </select>
            </div>
          </div>

          {/* Service Type */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              Service Type (Select all that apply)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {serviceTypes.map((service) => (
                <label
                  key={service}
                  className="flex items-center gap-2 cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    name="serviceType"
                    value={service}
                    checked={formData.serviceType.includes(service)}
                    onChange={() => handleServiceTypeChange(service)}
                    className="w-4 h-4 text-teal-500 border-slate-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="text-sm text-slate-700 group-hover:text-teal-500 transition-colors">
                    {service}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Start Date */}
          <div className="mb-6">
            <label htmlFor="startDate" className="block text-sm font-semibold text-slate-700 mb-2">
              Preferred Start Date
            </label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors text-sm cursor-pointer"
            />
          </div>

          {/* Additional Requirements */}
          <div className="mb-6">
            <label htmlFor="additionalRequirements" className="block text-sm font-semibold text-slate-700 mb-2">
              Additional Requirements
              <span className="text-slate-500 font-normal ml-2">(Max 500 characters)</span>
            </label>
            <textarea
              id="additionalRequirements"
              name="additionalRequirements"
              rows={4}
              maxLength={500}
              value={formData.additionalRequirements}
              onChange={(e) => setFormData({ ...formData, additionalRequirements: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 transition-colors text-sm resize-none"
              placeholder="Tell us about any specific requirements or concerns..."
            />
            <div className="text-right text-xs text-slate-500 mt-1">
              {formData.additionalRequirements.length}/500
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full md:w-3/5 mx-auto block bg-teal-500 hover:bg-teal-600 text-white py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Submitting...' : 'Request Free Quote'}
          </button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-center">
              <i className="ri-check-line text-xl mr-2"></i>
              Thank you! We'll contact you within 2 hours.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
              <i className="ri-error-warning-line text-xl mr-2"></i>
              Something went wrong. Please try again.
            </div>
          )}

          {/* Privacy Notice */}
          <p className="text-center text-xs text-slate-500 mt-4">
            By submitting this form, you agree to our privacy policy. We respect your privacy and will never share your information.
          </p>
        </form>
      </div>
    </section>
  );
}
